import type { Editor } from '@tiptap/core';
import {
  AlignCenter, AlignJustify, AlignLeft, AlignRight, Asterisk, Baseline, BetweenHorizontalEnd, BetweenHorizontalStart,
  BetweenVerticalEnd, BetweenVerticalStart, Bold, CaseSensitive, ClipboardPaste, Columns2, Copy, File,
  FileDigit, Grid2x2, Hash, Highlighter, History, Italic, Link, List, ListIndentDecrease, ListIndentIncrease, ListOrdered,
  Paintbrush, PanelBottom, PanelTop, RectangleVertical, Redo2, RemoveFormatting, Scan, Scissors, Search,
  SeparatorHorizontal, Sigma, Sparkles, SpellCheck, Stamp, Strikethrough, Subscript, Superscript, Table, TableCellsMerge,
  Download, FilePlus2, FileType, Info, Printer, Save, Share2, Timer, Trash2, Underline, Undo2,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  CORES_REALCE, CORES_TEXTO, ENTRELINHAS, ESTILOS, FONTES, ORDEM_ESTILOS, PREFIXO_PERSONALIZADO, TAMANHOS, fmtNum,
  formatoDoPronto, nomeDoEstilo, pilhaDaFonte, resumoDoFormato,
  type Alinhamento, type EstiloPersonalizado, type FormatoParagrafo, type RecuoEspecial,
} from '../estilos';
import { parseBRNumber } from '../formatos';
import type { FormatoArquivo } from '../arquivo';
import { Spinner } from '@/components/ui/spinner';
import { dicaCreditosDaRevisao } from '@/lib/ia/creditos';
import { MARGENS_PRONTAS, PAPEIS, type DocumentoForense, type Papel } from '../documento';
import type { TipoNumeracao, TipoQuebra } from '../extensoes/pecaForense';
import { Botao, BotaoNu, GradeTabela, Grupo, ItemMenu, MenuBotao, SepMenu, TextoMenu, TituloMenu } from './comum';

/** As abas do editor e as do app (`app:<chave>`, depois de Revisar — ex.: o Protocolo dos autos). */
export type Aba = 'arquivo' | 'inicio' | 'paragrafo' | 'inserir' | 'tabela' | 'layout' | 'revisar' | `app:${string}`;
export const ABAS: { chave: Aba; nome: string }[] = [
  { chave: 'arquivo', nome: 'Arquivo' },
  { chave: 'inicio', nome: 'Início' },
  { chave: 'paragrafo', nome: 'Parágrafo' },
  { chave: 'inserir', nome: 'Inserir' },
  { chave: 'tabela', nome: 'Tabela' },
  { chave: 'layout', nome: 'Layout' },
  { chave: 'revisar', nome: 'Revisar' },
];

export type SituacaoSalvamento = 'salvo' | 'sujo' | 'salvando' | 'erro' | 'novo';
export interface Salvamento {
  salvar: () => void;
  situacao: SituacaoSalvamento;
  /** Segundos entre salvamentos automáticos; 0 = desligado. */
  intervalo: number;
  definirIntervalo: (segundos: number) => void;
}
/** Quanto tempo depois de parar de digitar a peça se salva sozinha. */
export const INTERVALOS_SALVAMENTO: { segundos: number; rotulo: string }[] = [
  { segundos: 0, rotulo: 'Desligado' },
  { segundos: 3, rotulo: '3 segundos depois de parar de digitar' },
  { segundos: 10, rotulo: '10 segundos depois de parar de digitar' },
  { segundos: 30, rotulo: '30 segundos depois de parar de digitar' },
];

/** O que a barra precisa saber do editor, recalculado a cada transação. */
export interface EstadoEditor {
  /** Pronto (`corpo`) ou do escritório (`u:<id>`). */
  estilo: string;
  modificado: boolean;
  formato: FormatoParagrafo;
  negrito: boolean; italico: boolean; sublinhado: boolean; tachado: boolean; sobrescrito: boolean; subscrito: boolean;
  numerado: boolean; marcadores: boolean;
  noTabela: boolean;
  referencia: string | null;
  formulaCelula: string;
  palavras: number;
  podeDesfazer: boolean; podeRefazer: boolean;
  ortografiaAtiva: boolean; corretorNativo: boolean;
  pincel: boolean;
  corTexto: string; corRealce: string;
}

/** As ações que dependem da tela (painéis, área de transferência). */
export interface AcoesEditor {
  ajustar: (a: Partial<FormatoParagrafo>) => void;
  aplicarFonte: (nome: string) => void;
  aplicarTamanho: (pt: number) => void;
  copiar: () => void;
  recortar: () => void;
  colar: (modo: 'formatado' | 'texto') => void;
  caixa: (modo: 'alta' | 'baixa' | 'titulo') => void;
  alternarPincel: () => void;
  abrirParagrafo: () => void;
  abrirMargens: () => void;
  abrirTimbrado: () => void;
  /** Formatação do texto a partir de um arquivo; `null` quando o app não mede arquivo. */
  abrirFormatacao: (() => void) | null;
  editarCabecalhoRodape: () => void;
  abrirLateral: (qual: 'busca' | 'versoes', subst?: boolean) => void;
  /** Revisão por IA: o clique já revisa (a barra da revisão aparece embaixo). */
  revisarComIA: () => void;
  revisandoIA: boolean;
  /** Créditos que revisar o texto de agora debita, para a dica do botão. */
  creditosRevisaoIA: (() => number) | null;
  verificarOrtografia: () => void;
  definirOrtografia: (ativa: boolean) => void;
  definirCorretorNativo: (ativo: boolean) => void;
  dicionarioPessoal: string[];
  importarDicionario: (arquivo: File) => void;
  documento: DocumentoForense;
  mudarDocumento: (p: Partial<DocumentoForense>) => void;
  /**
   * A página com que as peças novas abrem (papel, orientação, margens); `null`
   * quando o app não a guarda. `ehEsta`: a desta peça já é a padrão; `guardada`:
   * há uma guardada (senão, vale a do app: A4, ABNT).
   */
  paginaPadrao: { ehEsta: boolean; guardada: boolean; usar: () => void; voltar: () => void } | null;
  temIA: boolean; temOrtografiaEmbutida: boolean;
  /** Presente quando o app diz como salvar (`onSalvar`). */
  salvamento: Salvamento | null;
  estilos: {
    personalizados: EstiloPersonalizado[];
    /** "Estilos do escritório" — o app pode dizer de quem são. */
    rotulo: string;
    /** O app sabe guardar estilos (props onSalvarEstilo…): mostra criar/atualizar/gerenciar. */
    editaveis: boolean;
    criar: () => void;
    atualizar: () => void;
    gerenciar: () => void;
  };
  /** Presente quando o app liga o link (o modo simples sempre liga). */
  inserirLink?: () => void;
  arquivo: AcoesArquivo;
}

/** A aba Arquivo. Ver `arquivo.ts`. */
export interface AcoesArquivo {
  /** Os formatos que o app oferece: o HTML sai do editor; DOCX e PDF, do servidor (`onGerarArquivo`). */
  formatos: FormatoArquivo[];
  baixar: (formato: FormatoArquivo) => void;
  /** Gerando um arquivo agora (spinner no Baixar; o botão fica inerte). */
  gerando: FormatoArquivo | null;
  imprimir: () => void;
  enviarPorEmail: () => void;
  copiarTexto: () => void;
  compartilhar: (formato: FormatoArquivo) => void;
  /** O navegador compartilha arquivo (celular, Safari, Edge…). */
  compartilhaArquivo: boolean;
  detalhes: () => { palavras: number; caracteres: number; semEspacos: number; paragrafos: number; paginas: number };
}

const ico = (I: typeof Bold) => <I className="cm-ef-ico" aria-hidden />;

export function Abas({ aba, aoTrocar, noTabela, extras, recolhida = false, abasDoApp = [] }: {
  aba: Aba; aoTrocar: (a: Aba) => void; noTabela: boolean; extras?: ReactNode;
  /** Barra recolhida: nenhuma aba aparece como aberta; clicar abre a dela. */
  recolhida?: boolean;
  /** Abas do app, depois das do editor. */
  abasDoApp?: { chave: string; nome: string }[];
}) {
  const todas: { chave: Aba; nome: string }[] = [...ABAS, ...abasDoApp.map((a) => ({ chave: `app:${a.chave}` as Aba, nome: a.nome }))];
  return (
    <div className="cm-ef-abas-linha">
      <div className="cm-ef-abas" role="tablist" aria-label="Barras de ferramentas">
        {todas.map((a) => (
          <BotaoNu
            key={a.chave}
            role="tab"
            aria-selected={!recolhida && aba === a.chave}
            aria-expanded={!recolhida && aba === a.chave}
            title={recolhida ? `Abrir a barra ${a.nome}` : aba === a.chave ? 'Recolher a barra' : undefined}
            className={`cm-ef-aba${a.chave === 'tabela' && noTabela && (recolhida || aba !== 'tabela') ? ' is-contexto' : ''}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => aoTrocar(a.chave)}
          >
            {a.nome}
          </BotaoNu>
        ))}
      </div>
      {extras && <div className="cm-ef-extras">{extras}</div>}
    </div>
  );
}

function ArquivoMenu({ rotulo, aceitar, aoEscolher }: { rotulo: ReactNode; aceitar: string; aoEscolher: (f: File) => void }) {
  return (
    <label className="cm-ef-op">
      <input type="file" accept={aceitar} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) aoEscolher(f); e.target.value = ''; }} />
      <span className="cm-ef-op-txt">{rotulo}</span>
    </label>
  );
}

/* ===================== Arquivo ===================== */

/** Os formatos, na mesma ordem em Baixar e em Compartilhar. */
export const FORMATOS_DO_ARQUIVO: Record<FormatoArquivo, { nome: string; detalhe: string }> = {
  docx: { nome: 'Word (.docx)', detalhe: 'Editável no Word, no LibreOffice e no Google Docs, com o timbrado' },
  pdf: { nome: 'PDF (.pdf)', detalhe: 'Pronto para protocolar ou enviar, com o timbrado' },
  html: { nome: 'Página da web (.html)', detalhe: 'Abre em qualquer navegador, sem o timbrado' },
};

export function BarraArquivo({ ac }: { ac: AcoesEditor }) {
  const a = ac.arquivo;
  const ocupado = a.gerando !== null;
  return (
    <div className="cm-ef-barra" role="toolbar" aria-label="Arquivo">
      <Grupo>
        {ac.salvamento && (
          <Botao dica={{ nome: 'Salvar', atalho: 'Shift+F12' }} icone={ico(Save)} rotulo="Salvar"
            desativado={ac.salvamento.situacao === 'salvando'} ativo={ac.salvamento.situacao === 'sujo' ? true : undefined} onClick={ac.salvamento.salvar} />
        )}
        <Botao dica={{ nome: 'Versões', desc: 'As versões guardadas da peça: compare com o texto atual e volte a uma anterior.' }} icone={ico(History)} rotulo="Versões" onClick={() => ac.abrirLateral('versoes')} />
      </Grupo>
      <Grupo>
        <MenuBotao dica={{ nome: 'Baixar', desc: 'Uma cópia da peça neste computador, no formato escolhido.' }}
          icone={ocupado ? <Spinner size={16} className="text-inherit" label="Gerando o arquivo" /> : ico(Download)} rotulo="Baixar" largura={320}>
          {(fechar) => (
            <>
              <TituloMenu>Baixar como</TituloMenu>
              {a.formatos.map((f) => (
                <ItemMenu key={f} detalhe={FORMATOS_DO_ARQUIVO[f].detalhe} onClick={() => { if (ocupado) return; fechar(); a.baixar(f); }}>{FORMATOS_DO_ARQUIVO[f].nome}</ItemMenu>
              ))}
            </>
          )}
        </MenuBotao>
        <Botao dica={{ nome: 'Imprimir', desc: 'Só a peça, com o timbrado, sem o resto da tela. Para um PDF, escolha “Salvar como PDF”.' }} icone={ico(Printer)} rotulo="Imprimir" onClick={a.imprimir} />
        <MenuBotao dica={{ nome: 'Compartilhar', desc: 'Mandar a peça em Word, PDF ou página da web, ou copiar o texto.' }} icone={ico(Share2)} rotulo="Compartilhar" largura={320}>
          {(fechar) => (
            <>
              <TituloMenu>{a.compartilhaArquivo ? 'Enviar por WhatsApp, e-mail, AirDrop…' : 'Enviar o arquivo'}</TituloMenu>
              {a.formatos.map((f) => (
                <ItemMenu key={f} onClick={() => { if (ocupado) return; fechar(); a.compartilhar(f); }}>{`Em ${FORMATOS_DO_ARQUIVO[f].nome}`}</ItemMenu>
              ))}
              {!a.compartilhaArquivo && <TextoMenu>Neste navegador o arquivo é baixado: anexe-o na mensagem. No celular, abre o menu de compartilhar.</TextoMenu>}
              <SepMenu />
              <ItemMenu detalhe="Abre o seu programa de e-mail com o texto" onClick={() => { fechar(); a.enviarPorEmail(); }}>Enviar o texto por e-mail</ItemMenu>
              <ItemMenu detalhe="Para colar em e-mail, mensagem ou outro sistema" onClick={() => { fechar(); a.copiarTexto(); }}>Copiar o texto todo</ItemMenu>
            </>
          )}
        </MenuBotao>
      </Grupo>
      <Grupo>
        <Botao dica={{ nome: 'Configurar página', desc: 'Margens da peça. Papel e orientação ficam na aba Layout.' }} icone={ico(RectangleVertical)} rotulo="Configurar página" onClick={ac.abrirMargens} />
        <Botao dica={{ nome: 'Papel timbrado', desc: 'O timbrado do escritório no cabeçalho e no rodapé. Cadastre uma vez; escolha em cada peça.' }} icone={ico(Stamp)} rotulo="Papel timbrado" onClick={ac.abrirTimbrado} />
        {ac.abrirFormatacao && (
          <Botao dica={{ nome: 'Formatação do texto', desc: 'A fonte, o parágrafo e as margens de uma petição sua (Word ou PDF), nesta peça ou como modelo.' }}
            icone={ico(FileType)} rotulo="Formatação do texto" onClick={ac.abrirFormatacao} />
        )}
        <MenuBotao dica={{ nome: 'Detalhes', desc: 'Palavras, caracteres, parágrafos e páginas.' }} icone={ico(Info)} rotulo="Detalhes" largura={260}>
          {() => {
            const d = a.detalhes();
            const n = (v: number) => v.toLocaleString('pt-BR');
            return (
              <>
                <TituloMenu>Detalhes da peça</TituloMenu>
                <span className="cm-ef-op cm-ef-op-txt is-estatico">Páginas: {n(d.paginas)}</span>
                <span className="cm-ef-op cm-ef-op-txt is-estatico">Palavras: {n(d.palavras)}</span>
                <span className="cm-ef-op cm-ef-op-txt is-estatico">Caracteres (sem espaços): {n(d.semEspacos)}</span>
                <span className="cm-ef-op cm-ef-op-txt is-estatico">Caracteres (com espaços): {n(d.caracteres)}</span>
                <span className="cm-ef-op cm-ef-op-txt is-estatico">Parágrafos: {n(d.paragrafos)}</span>
                <SepMenu />
                <span className="cm-ef-op cm-ef-op-txt is-estatico">Papel: {PAPEIS[ac.documento.papel].nome} {ac.documento.orientacao} · margens {ac.documento.nomeMargens}</span>
              </>
            );
          }}
        </MenuBotao>
      </Grupo>
    </div>
  );
}

/* ===================== Início ===================== */

export function BarraInicio({ ed, est, ac }: { ed: Editor; est: EstadoEditor; ac: AcoesEditor }) {
  const f = est.formato;
  const alinhar = (a: Alinhamento, nome: string, atalho: string, I: typeof Bold) => (
    <Botao dica={{ nome, atalho }} icone={ico(I)} ativo={f.alinhamento === a} onClick={() => ac.ajustar({ alinhamento: a })} />
  );
  return (
    <div className="cm-ef-barra" role="toolbar" aria-label="Início">
      <Grupo>
        {ac.salvamento && (
          <>
            <Botao dica={{ nome: 'Salvar', atalho: 'Shift+F12', desc: 'Ctrl+B, o Salvar do Word, aqui é o Negrito.' }} icone={ico(Save)}
              desativado={ac.salvamento.situacao === 'salvando'} ativo={ac.salvamento.situacao === 'sujo' ? true : undefined} onClick={ac.salvamento.salvar} />
            <MenuBotao dica={{ nome: 'Salvamento automático', desc: 'Quanto tempo depois de você parar de digitar o texto se salva sozinho.' }} icone={ico(Timer)}>
              {(fechar) => (
                <>
                  <TituloMenu>Salvamento automático</TituloMenu>
                  {INTERVALOS_SALVAMENTO.map((o) => (
                    <ItemMenu key={o.segundos} marcado={ac.salvamento!.intervalo === o.segundos} onClick={() => { fechar(); ac.salvamento!.definirIntervalo(o.segundos); }}>{o.rotulo}</ItemMenu>
                  ))}
                </>
              )}
            </MenuBotao>
          </>
        )}
        <Botao dica={{ nome: 'Desfazer', atalho: 'Ctrl+Z' }} icone={ico(Undo2)} desativado={!est.podeDesfazer} onClick={() => ed.chain().focus().undo().run()} />
        <Botao dica={{ nome: 'Refazer', atalho: ['Ctrl+R', 'Ctrl+Y'] }} icone={ico(Redo2)} desativado={!est.podeRefazer} onClick={() => ed.chain().focus().redo().run()} />
      </Grupo>
      <Grupo>
        <Botao dica={{ nome: 'Recortar', atalho: 'Ctrl+X' }} icone={ico(Scissors)} onClick={ac.recortar} />
        <Botao dica={{ nome: 'Copiar', atalho: 'Ctrl+C' }} icone={ico(Copy)} onClick={ac.copiar} />
        <MenuBotao dica={{ nome: 'Colar', atalho: 'Ctrl+V', desc: 'Mantém negrito, itálico e listas; descarta fontes e margens de fora.' }} icone={ico(ClipboardPaste)}>
          {(fechar) => (
            <>
              <ItemMenu atalho="Ctrl+V" onClick={() => { fechar(); ac.colar('formatado'); }}>Colar</ItemMenu>
              <ItemMenu atalho="Ctrl+Shift+V" onClick={() => { fechar(); ac.colar('texto'); }}>Colar só o texto</ItemMenu>
              <SepMenu />
              <TextoMenu>Colar mantém negrito, itálico e listas e descarta fontes e margens vindas do Word.</TextoMenu>
            </>
          )}
        </MenuBotao>
      </Grupo>
      <Grupo>
        <MenuBotao seletor largura={420} className="cm-ef-sel-estilo" dica={{ nome: 'Estilo do parágrafo', desc: 'Fonte, recuos, entrelinha e espaçamento de uma vez.' }}
          rotulo={`${nomeDoEstilo(est.estilo)}${est.modificado ? ' *' : ''}`}>
          {(fechar) => (
            <>
              {(ac.estilos.personalizados.length > 0 || ac.estilos.editaveis) && <TituloMenu>Estilos prontos</TituloMenu>}
              {ORDEM_ESTILOS.map((k) => {
                const fp = formatoDoPronto(k);
                // Com a seção do escritório no menu, os prontos ficam compactos (amostra só no
                // estilo em uso) — senão o que é do escritório some abaixo.
                const compacto = (ac.estilos.personalizados.length > 0 || ac.estilos.editaveis) && k !== est.estilo;
                return <ItemEstilo key={k} nome={ESTILOS[k].nome} resumo={resumoDoFormato(fp)} formato={fp} amostra={compacto ? '' : ESTILOS[k].amostra}
                  ativo={k === est.estilo} aoEscolher={() => { fechar(); ed.chain().focus().definirEstilo(k).run(); }} />;
              })}
              {ac.estilos.personalizados.length > 0 && (
                <>
                  <SepMenu />
                  <TituloMenu>{ac.estilos.rotulo}</TituloMenu>
                  {ac.estilos.personalizados.map((p) => {
                    const fp = { ...formatoDoPronto(p.baseadoEm), ...p.ajustes };
                    const id = `${PREFIXO_PERSONALIZADO}${p.id}`;
                    return <ItemEstilo key={p.id} nome={p.nome} resumo={resumoDoFormato(fp)} formato={fp} amostra={ESTILOS[p.baseadoEm].amostra}
                      ativo={id === est.estilo} aoEscolher={() => { fechar(); ed.chain().focus().definirEstilo(id).run(); }} />;
                  })}
                </>
              )}
              {ac.estilos.editaveis && (
                <>
                  <SepMenu />
                  <ItemMenu detalhe="Guarda fonte, tamanho, alinhamento, recuos e espaçamento deste parágrafo" onClick={() => { fechar(); ac.estilos.criar(); }}>Criar estilo a partir deste parágrafo…</ItemMenu>
                  {est.modificado && (
                    <ItemMenu detalhe="Os parágrafos já escritos nesse estilo mudam junto" onClick={() => { fechar(); ac.estilos.atualizar(); }}>Atualizar “{nomeDoEstilo(est.estilo)}” para corresponder a este parágrafo</ItemMenu>
                  )}
                  <ItemMenu onClick={() => { fechar(); ac.estilos.gerenciar(); }}>Gerenciar estilos…</ItemMenu>
                </>
              )}
            </>
          )}
        </MenuBotao>
      </Grupo>
      <Grupo>
        <MenuBotao seletor className="cm-ef-sel-fonte" dica={{ nome: 'Fonte', atalho: 'Ctrl+Shift+F' }} rotulo={f.fonte}>
          {(fechar) => FONTES.map((x) => (
            <ItemMenu key={x.nome} ativo={x.nome === f.fonte} onClick={() => { fechar(); ac.aplicarFonte(x.nome); }}>
              <span style={{ fontFamily: x.pilha, fontSize: 15 }}>{x.nome}</span>
            </ItemMenu>
          ))}
        </MenuBotao>
        <MenuBotao seletor className="cm-ef-sel-tam" dica={{ nome: 'Tamanho da fonte', atalho: ['Ctrl+Shift+>', 'Ctrl+Shift+<'], desc: 'Aumentar ou diminuir' }} rotulo={fmtNum(f.tamanho)}>
          {(fechar) => TAMANHOS.map((t) => <ItemMenu key={t} ativo={t === f.tamanho} onClick={() => { fechar(); ac.aplicarTamanho(t); }}>{t} pt</ItemMenu>)}
        </MenuBotao>
      </Grupo>
      <Grupo>
        <Botao dica={{ nome: 'Negrito', atalho: ['Ctrl+N', 'Ctrl+B'], desc: 'Ctrl+N é do Word; o navegador o reserva para nova janela, então Ctrl+B também vale.' }} icone={ico(Bold)} ativo={est.negrito} onClick={() => ed.chain().focus().toggleBold().run()} />
        <Botao dica={{ nome: 'Itálico', atalho: 'Ctrl+I' }} icone={ico(Italic)} ativo={est.italico} onClick={() => ed.chain().focus().toggleItalic().run()} />
        <Botao dica={{ nome: 'Sublinhado', atalho: 'Ctrl+S' }} icone={ico(Underline)} ativo={est.sublinhado} onClick={() => ed.chain().focus().toggleUnderline().run()} />
        <Botao dica={{ nome: 'Tachado' }} icone={ico(Strikethrough)} ativo={est.tachado} onClick={() => ed.chain().focus().toggleStrike().run()} />
        <Botao dica={{ nome: 'Sobrescrito', atalho: 'Ctrl+Shift++', desc: 'Para 1º, nº e chamadas de nota.' }} icone={ico(Superscript)} ativo={est.sobrescrito} onClick={() => ed.chain().focus().toggleSuperscript().run()} />
        <Botao dica={{ nome: 'Subscrito', atalho: 'Ctrl+=' }} icone={ico(Subscript)} ativo={est.subscrito} onClick={() => ed.chain().focus().toggleSubscript().run()} />
        <MenuBotao dica={{ nome: 'Maiúsculas e minúsculas', atalho: 'Shift+F3' }} icone={ico(CaseSensitive)}>
          {(fechar) => (
            <>
              <ItemMenu onClick={() => { fechar(); ac.caixa('alta'); }}>MAIÚSCULAS</ItemMenu>
              <ItemMenu onClick={() => { fechar(); ac.caixa('baixa'); }}>minúsculas</ItemMenu>
              <ItemMenu onClick={() => { fechar(); ac.caixa('titulo'); }}>Primeira Letra Maiúscula</ItemMenu>
              <TextoMenu>Shift+F3 alterna entre os três no trecho selecionado.</TextoMenu>
            </>
          )}
        </MenuBotao>
      </Grupo>
      <Grupo>
        <MenuBotao dica={{ nome: 'Cor da fonte' }} icone={<span className="cm-ef-cor-ico">{ico(Baseline)}<i style={{ background: est.corTexto }} /></span>}>
          {(fechar) => (
            <>
              <TituloMenu>Cor da fonte</TituloMenu>
              <div className="cm-ef-cores">
                {CORES_TEXTO.map((c) => <BotaoNu key={c} className="cm-ef-cor" style={{ background: c }} aria-label={`Cor ${c}`} onClick={() => { fechar(); ed.chain().focus().setColor(c).run(); }} />)}
              </div>
              <ItemMenu onClick={() => { fechar(); ed.chain().focus().unsetColor().run(); }}>Cor automática</ItemMenu>
            </>
          )}
        </MenuBotao>
        <MenuBotao dica={{ nome: 'Cor do realce do texto' }} icone={<span className="cm-ef-cor-ico">{ico(Highlighter)}<i style={{ background: est.corRealce }} /></span>}>
          {(fechar) => (
            <>
              <TituloMenu>Cor do realce</TituloMenu>
              <div className="cm-ef-cores">
                {CORES_REALCE.map((c) => <BotaoNu key={c} className="cm-ef-cor" style={{ background: c }} aria-label={`Realce ${c}`} onClick={() => { fechar(); ed.chain().focus().setHighlight({ color: c }).run(); }} />)}
                <BotaoNu className="cm-ef-cor cm-ef-cor-nenhuma" aria-label="Sem realce" onClick={() => { fechar(); ed.chain().focus().unsetHighlight().run(); }} />
              </div>
            </>
          )}
        </MenuBotao>
      </Grupo>
      <Grupo>
        {alinhar('left', 'Alinhar à esquerda', 'Ctrl+Q', AlignLeft)}
        {alinhar('center', 'Centralizar', 'Ctrl+E', AlignCenter)}
        {alinhar('right', 'Alinhar à direita', 'Ctrl+G', AlignRight)}
        {alinhar('justify', 'Justificar', 'Ctrl+J', AlignJustify)}
      </Grupo>
      <Grupo>
        {/* Entrelinha e espaços moram na aba Parágrafo; Ctrl+1/5/2 valem em qualquer aba. */}
        <Botao dica={{ nome: 'Diminuir recuo', atalho: 'Ctrl+Shift+M' }} icone={ico(ListIndentDecrease)} onClick={() => ac.ajustar({ recuoEsquerdo: Math.max(0, f.recuoEsquerdo - 1.25) })} />
        <Botao dica={{ nome: 'Aumentar recuo', atalho: 'Ctrl+M' }} icone={ico(ListIndentIncrease)} onClick={() => ac.ajustar({ recuoEsquerdo: Math.min(10, f.recuoEsquerdo + 1.25) })} />
      </Grupo>
      <Grupo>
        <MenuBotao dica={{ nome: 'Numeração', desc: 'I, II, III · 1, 2, 3 · a), b), c)' }} icone={ico(ListOrdered)} ativo={est.numerado}>
          {(fechar) => <MenuNumeracao ed={ed} fechar={fechar} />}
        </MenuBotao>
        <Botao dica={{ nome: 'Marcadores', atalho: 'Ctrl+Shift+L' }} icone={ico(List)} ativo={est.marcadores} onClick={() => ed.chain().focus().toggleBulletList().run()} />
      </Grupo>
      <Grupo>
        <Botao dica={{ nome: 'Pincel de formatação', atalho: 'Ctrl+Shift+C', desc: 'Copia a formatação deste parágrafo para o próximo que você clicar.' }} icone={ico(Paintbrush)} ativo={est.pincel} variante={est.pincel ? 'primario' : 'normal'} onClick={ac.alternarPincel} />
        <Botao dica={{ nome: 'Limpar formatação', atalho: 'Ctrl+Espaço', desc: 'Volta o parágrafo ao estilo Corpo da peça.' }} icone={ico(RemoveFormatting)} onClick={() => ed.chain().focus().limparFormatacao().run()} />
        <Botao dica={{ nome: 'Localizar e substituir', atalho: ['Ctrl+L', 'Ctrl+U'] }} icone={ico(Search)} onClick={() => ac.abrirLateral('busca')} />
      </Grupo>
    </div>
  );
}

function ItemEstilo({ nome, resumo, formato: e, amostra, ativo, aoEscolher }: {
  nome: string; resumo: string; formato: FormatoParagrafo; amostra: string; ativo: boolean; aoEscolher: () => void;
}) {
  const recuo = e.especial === 'primeira' ? { textIndent: e.medida * 7 } : e.especial === 'deslocado' ? { textIndent: -e.medida * 7, paddingLeft: e.medida * 7 } : {};
  return (
    <BotaoNu role="menuitem" className={`cm-ef-op cm-ef-op-estilo${ativo ? ' is-ativo' : ''}`} onClick={aoEscolher}>
      <span className="cm-ef-op-estilo-nome">{nome}<small>{resumo}</small></span>
      {amostra && <span className="cm-ef-amostra">
        <span style={{ display: 'block', fontFamily: pilhaDaFonte(e.fonte), fontSize: Math.max(11, e.tamanho * 0.9), textAlign: e.alinhamento, marginLeft: e.recuoEsquerdo * 7, lineHeight: e.entrelinha, fontWeight: e.negrito ? 700 : 400, fontStyle: e.italico ? 'italic' : 'normal', ...recuo }}>
          {amostra}
        </span>
      </span>}
    </BotaoNu>
  );
}

export function MenuEntrelinha({ f, ac, fechar }: { f: FormatoParagrafo; ac: AcoesEditor; fechar: () => void }) {
  const ciclo = [0, 6, 12, 18];
  const proximo = (v: number) => ciclo[(ciclo.indexOf(v) + 1) % ciclo.length];
  return (
    <>
      <TituloMenu>Entrelinha</TituloMenu>
      {ENTRELINHAS.map((e) => <ItemMenu key={e.valor} atalho={e.atalho} ativo={e.valor === f.entrelinha} onClick={() => { fechar(); ac.ajustar({ entrelinha: e.valor }); }}>{e.rotulo}</ItemMenu>)}
      <SepMenu />
      <ItemMenu atalho="Ctrl+0" detalhe={`${fmtNum(f.espacoAntes)} pt`} onClick={() => ac.ajustar({ espacoAntes: proximo(f.espacoAntes) })}>Espaço antes</ItemMenu>
      <ItemMenu detalhe={`${fmtNum(f.espacoDepois)} pt`} onClick={() => ac.ajustar({ espacoDepois: proximo(f.espacoDepois) })}>Espaço depois</ItemMenu>
      <SepMenu />
      <ItemMenu onClick={() => { fechar(); ac.abrirParagrafo(); }}>Mais opções de espaçamento…</ItemMenu>
    </>
  );
}

export function MenuNumeracao({ ed, fechar }: { ed: Editor; fechar: () => void }) {
  const op = (tipo: TipoNumeracao, rotulo: string, uso: string) => (
    <ItemMenu detalhe={uso} onClick={() => { fechar(); ed.chain().focus().numerar(tipo).run(); }}>{rotulo}</ItemMenu>
  );
  return (
    <>
      <TituloMenu>Numeração</TituloMenu>
      {op('romano', 'I, II, III', 'seções')}
      {op('decimal', '1, 2, 3', 'itens e quesitos')}
      {op('alfa', 'a), b), c)', 'pedidos')}
      {op('multinivel', '1.1, 1.2', 'subtópicos')}
      <SepMenu />
      <ItemMenu onClick={() => { fechar(); ed.chain().focus().desnumerar().run(); }}>Sem numeração</ItemMenu>
    </>
  );
}

/* ===================== Inserir ===================== */

export function BarraInserir({ ed, ac }: { ed: Editor; ac: AcoesEditor }) {
  const d = ac.documento;
  const pag = (p: Partial<DocumentoForense['paginas']>) => ac.mudarDocumento({ paginas: { ...d.paginas, ...p } });
  const fls = (p: Partial<DocumentoForense['folhas']>) => ac.mudarDocumento({ folhas: { ...d.folhas, ...p } });
  return (
    <div className="cm-ef-barra" role="toolbar" aria-label="Inserir">
      <Grupo>
        <MenuBotao dica={{ nome: 'Tabela', desc: 'Escolha linhas e colunas. As ferramentas ficam na aba Tabela.' }} icone={ico(Table)} rotulo="Tabela">
          {(fechar) => <><TituloMenu>Inserir tabela</TituloMenu><GradeTabela aoEscolher={(l, c) => { fechar(); ed.chain().focus().insertTable({ rows: l, cols: c, withHeaderRow: true }).run(); }} /></>}
        </MenuBotao>
      </Grupo>
      <Grupo>
        {ac.inserirLink && <Botao dica={{ nome: 'Link', atalho: 'Ctrl+K' }} icone={ico(Link)} rotulo="Link" ativo={ed.isActive('link')} onClick={ac.inserirLink} />}
        <Botao dica={{ nome: 'Nota de rodapé', atalho: 'Ctrl+Alt+F', desc: 'Número no texto e a nota no pé da página, numeradas sozinhas.' }} icone={ico(Asterisk)} rotulo="Nota de rodapé" onClick={() => ed.chain().focus().inserirNota().run()} />
      </Grupo>
      <Grupo>
        <Botao dica={{ nome: 'Cabeçalho', desc: 'Texto no alto de todas as páginas, por cima do timbrado.' }} icone={ico(PanelTop)} rotulo="Cabeçalho" onClick={ac.editarCabecalhoRodape} />
        <Botao dica={{ nome: 'Rodapé', desc: 'Texto no pé de todas as páginas.' }} icone={ico(PanelBottom)} rotulo="Rodapé" onClick={ac.editarCabecalhoRodape} />
        <MenuBotao dica={{ nome: 'Número de página', desc: 'Posição, formato e número inicial.' }} icone={ico(Hash)} rotulo="Número de página">
          {(fechar) => (
            <>
              <ItemMenu marcado={d.paginas.ativa} onClick={() => pag({ ativa: !d.paginas.ativa })}>Numerar as páginas</ItemMenu>
              <SepMenu />
              <TituloMenu>Posição</TituloMenu>
              <ItemMenu ativo={d.paginas.posicao === 'rodapeDireita'} onClick={() => { fechar(); pag({ ativa: true, posicao: 'rodapeDireita' }); }}>Rodapé, à direita</ItemMenu>
              <ItemMenu ativo={d.paginas.posicao === 'rodapeCentro'} onClick={() => { fechar(); pag({ ativa: true, posicao: 'rodapeCentro' }); }}>Rodapé, ao centro</ItemMenu>
              <ItemMenu ativo={d.paginas.posicao === 'cabecalhoDireita'} onClick={() => { fechar(); pag({ ativa: true, posicao: 'cabecalhoDireita' }); }}>Cabeçalho, à direita</ItemMenu>
              <SepMenu />
              <TituloMenu>Formato</TituloMenu>
              <ItemMenu ativo={d.paginas.formato === 'paginaDeTotal'} onClick={() => { fechar(); pag({ ativa: true, formato: 'paginaDeTotal' }); }}>Página 1 de 2</ItemMenu>
              <ItemMenu ativo={d.paginas.formato === 'numero'} onClick={() => { fechar(); pag({ ativa: true, formato: 'numero' }); }}>1</ItemMenu>
              <ItemMenu ativo={d.paginas.formato === 'traco'} onClick={() => { fechar(); pag({ ativa: true, formato: 'traco' }); }}>– 1 –</ItemMenu>
              <SepMenu />
              <NumeroMenu rotulo="Começar em" valor={d.paginas.inicio} aoMudar={(v) => pag({ inicio: v })} />
              <ItemMenu marcado={d.paginas.primeiraSemNumero} onClick={() => pag({ primeiraSemNumero: !d.paginas.primeiraSemNumero })}>Primeira página sem número</ItemMenu>
            </>
          )}
        </MenuBotao>
        <MenuBotao dica={{ nome: 'Numeração de folhas', desc: '“fls.” no canto superior direito, a partir do número que você escolher, para continuar a numeração dos autos.' }} icone={ico(FileDigit)} rotulo="Numeração de folhas">
          {(fechar) => (
            <>
              <ItemMenu marcado={d.folhas.ativa} onClick={() => fls({ ativa: !d.folhas.ativa })}>Numerar as folhas</ItemMenu>
              <SepMenu />
              <TituloMenu>Formato</TituloMenu>
              <ItemMenu ativo={d.folhas.formato === 'fls'} onClick={() => { fechar(); fls({ ativa: true, formato: 'fls' }); }}>fls. 12</ItemMenu>
              <ItemMenu ativo={d.folhas.formato === 'fl'} onClick={() => { fechar(); fls({ ativa: true, formato: 'fl' }); }}>Fl. 12</ItemMenu>
              <ItemMenu ativo={d.folhas.formato === 'folha'} onClick={() => { fechar(); fls({ ativa: true, formato: 'folha' }); }}>Folha 12</ItemMenu>
              <SepMenu />
              <NumeroMenu rotulo="Primeira folha" valor={d.folhas.inicio} aoMudar={(v) => fls({ ativa: true, inicio: v })} />
              <TextoMenu>Para juntar a peça aos autos continuando a numeração: se os autos param na folha 11, comece em 12.</TextoMenu>
            </>
          )}
        </MenuBotao>
      </Grupo>
    </div>
  );
}

function NumeroMenu({ rotulo, valor, aoMudar }: { rotulo: string; valor: number; aoMudar: (v: number) => void }) {
  const [v, setV] = useState(String(valor));
  // O valor mudou por fora: o campo acompanha (ajuste no render, sem efeito).
  const [valorVisto, setValorVisto] = useState(valor);
  if (valor !== valorVisto) { setValorVisto(valor); setV(String(valor)); }
  return (
    <label className="cm-ef-linha-num">
      {rotulo}
      <input type="number" min={1} value={v} onChange={(e) => { setV(e.target.value); const n = Number(e.target.value); if (n >= 1) aoMudar(Math.floor(n)); }} />
    </label>
  );
}

/* ===================== Parágrafo ===================== */

// Campo de medida da barra: aplica no Enter ou ao sair, não a cada tecla — o
// ajuste devolve o foco ao texto, e aplicar enquanto se digita "1,5" cortaria
// no "1". Aceita vírgula.
function CampoMedida({ rotulo, valor, unidade, min, max, aoMudar }: {
  rotulo: string; valor: number; unidade: string; min: number; max: number; aoMudar: (v: number) => void;
}) {
  const [v, setV] = useState(fmtNum(valor));
  const [valorVisto, setValorVisto] = useState(valor);
  if (valor !== valorVisto) { setValorVisto(valor); setV(fmtNum(valor)); }
  const aplicar = () => {
    const n = parseBRNumber(v);
    if (Number.isFinite(n) && n >= min && n <= max && n !== valor) aoMudar(n);
    else setV(fmtNum(valor));
  };
  return (
    <label className="cm-ef-medida">
      <span>{rotulo}</span>
      <input inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} onBlur={aplicar} aria-label={`${rotulo} (${unidade})`}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aplicar(); } if (e.key === 'Escape') setV(fmtNum(valor)); }} />
      <em>{unidade}</em>
    </label>
  );
}

const ESPECIAIS: { valor: RecuoEspecial; rotulo: string }[] = [
  { valor: 'nenhum', rotulo: 'Nenhum' },
  { valor: 'primeira', rotulo: 'Primeira linha' },
  { valor: 'deslocado', rotulo: 'Deslocado' },
];

/** As opções do painel Parágrafo à vista, numa faixa. Vale para o parágrafo do cursor. */
export function BarraParagrafo({ est, ac }: { est: EstadoEditor; ac: AcoesEditor }) {
  const f = est.formato;
  const entreLista = ENTRELINHAS.some((x) => x.valor === f.entrelinha);
  // Sem os alinhamentos: moram na Início (nada repetido entre abas).
  return (
    <div className="cm-ef-barra" role="toolbar" aria-label="Parágrafo">
      <Grupo className="cm-ef-grupo-medidas">
        <CampoMedida rotulo="Recuo à esquerda" valor={f.recuoEsquerdo} unidade="cm" min={0} max={10} aoMudar={(v) => ac.ajustar({ recuoEsquerdo: v })} />
        <CampoMedida rotulo="à direita" valor={f.recuoDireito} unidade="cm" min={0} max={8} aoMudar={(v) => ac.ajustar({ recuoDireito: v })} />
        <label className="cm-ef-medida">
          <span>Especial</span>
          <select value={f.especial} onChange={(e) => {
            const especial = e.target.value as RecuoEspecial;
            ac.ajustar({ especial, medida: especial === 'nenhum' ? 0 : f.medida || (especial === 'primeira' ? 3 : 0.75) });
          }}>
            {ESPECIAIS.map((x) => <option key={x.valor} value={x.valor}>{x.rotulo}</option>)}
          </select>
        </label>
        {f.especial !== 'nenhum' && (
          <CampoMedida rotulo="Medida" valor={f.medida} unidade="cm" min={0} max={8} aoMudar={(v) => ac.ajustar({ medida: v })} />
        )}
      </Grupo>
      <Grupo className="cm-ef-grupo-medidas">
        <CampoMedida rotulo="Espaço antes" valor={f.espacoAntes} unidade="pt" min={0} max={72} aoMudar={(v) => ac.ajustar({ espacoAntes: v })} />
        <CampoMedida rotulo="depois" valor={f.espacoDepois} unidade="pt" min={0} max={72} aoMudar={(v) => ac.ajustar({ espacoDepois: v })} />
        <label className="cm-ef-medida">
          <span>Entrelinha</span>
          <select value={entreLista ? String(f.entrelinha) : 'outra'} onChange={(e) => { if (e.target.value !== 'outra') ac.ajustar({ entrelinha: Number(e.target.value) }); }}>
            {ENTRELINHAS.map((x) => <option key={x.valor} value={String(x.valor)}>{x.rotulo}</option>)}
            {!entreLista && <option value="outra">{fmtNum(f.entrelinha)}</option>}
          </select>
        </label>
        <CampoMedida rotulo="Em" valor={f.entrelinha} unidade="×" min={0.8} max={4} aoMudar={(v) => ac.ajustar({ entrelinha: v })} />
      </Grupo>
    </div>
  );
}

/* ===================== Tabela ===================== */

export function BarraTabela({ ed, est }: { ed: Editor; est: EstadoEditor }) {
  const [formula, setFormula] = useState(est.formulaCelula);
  // Outra célula (ou a fórmula mudou): a barra mostra a dela.
  const celulaAtual = `${est.referencia ?? ''}\u0000${est.formulaCelula}`;
  const [celulaVista, setCelulaVista] = useState(celulaAtual);
  if (celulaAtual !== celulaVista) { setCelulaVista(celulaAtual); setFormula(est.formulaCelula); }
  const naCelula = (fn: () => void) => () => { if (!est.noTabela) return; fn(); };
  const alinharCelula = (a: string) => naCelula(() => ed.chain().focus().setCellAttribute('alinhamento', a).run());
  const fora = !est.noTabela;
  return (
    <div className="cm-ef-barra" role="toolbar" aria-label="Tabela">
      <Grupo>
        <Botao dica={{ nome: 'Inserir linha acima' }} icone={ico(BetweenHorizontalStart)} desativado={fora} onClick={() => ed.chain().focus().addRowBefore().run()} />
        <Botao dica={{ nome: 'Inserir linha abaixo' }} icone={ico(BetweenHorizontalEnd)} desativado={fora} onClick={() => ed.chain().focus().addRowAfter().run()} />
        <Botao dica={{ nome: 'Inserir coluna à esquerda' }} icone={ico(BetweenVerticalStart)} desativado={fora} onClick={() => ed.chain().focus().addColumnBefore().run()} />
        <Botao dica={{ nome: 'Inserir coluna à direita' }} icone={ico(BetweenVerticalEnd)} desativado={fora} onClick={() => ed.chain().focus().addColumnAfter().run()} />
      </Grupo>
      <Grupo>
        <Botao dica={{ nome: 'Excluir linha' }} icone={<span className="cm-ef-ico-txt">−L</span>} desativado={fora} onClick={() => ed.chain().focus().deleteRow().run()} />
        <Botao dica={{ nome: 'Excluir coluna' }} icone={<span className="cm-ef-ico-txt">−C</span>} desativado={fora} onClick={() => ed.chain().focus().deleteColumn().run()} />
        <Botao dica={{ nome: 'Excluir tabela', desc: 'Ctrl+Z desfaz.' }} icone={ico(Trash2)} variante="perigo" desativado={fora} onClick={() => ed.chain().focus().deleteTable().run()} />
      </Grupo>
      <Grupo>
        <Botao dica={{ nome: 'Mesclar células', desc: 'Selecione duas ou mais células com Shift e mescle.' }} icone={ico(TableCellsMerge)} desativado={fora} onClick={() => ed.chain().focus().mergeOrSplit().run()} />
        <Botao dica={{ nome: 'Alinhar célula à esquerda' }} icone={ico(AlignLeft)} desativado={fora} onClick={alinharCelula('left')} />
        <Botao dica={{ nome: 'Centralizar na célula' }} icone={ico(AlignCenter)} desativado={fora} onClick={alinharCelula('center')} />
        <Botao dica={{ nome: 'Alinhar célula à direita', desc: 'Para valores: os algarismos ficam no prumo.' }} icone={ico(AlignRight)} desativado={fora} onClick={alinharCelula('right')} />
        <MenuBotao dica={{ nome: 'Bordas e cabeçalho' }} icone={ico(Grid2x2)} desativado={fora}>
          {(fechar) => (
            <>
              <TituloMenu>Bordas</TituloMenu>
              {(['todas', 'externas', 'nenhuma'] as const).map((b) => (
                <ItemMenu key={b} onClick={() => { fechar(); ed.chain().focus().updateAttributes('table', { bordas: b }).run(); }}>
                  {b === 'todas' ? 'Todas as bordas' : b === 'externas' ? 'Só as externas' : 'Sem bordas'}
                </ItemMenu>
              ))}
              <SepMenu />
              <ItemMenu marcado={!!ed.getAttributes('table').sombrear} onClick={() => { fechar(); ed.chain().focus().updateAttributes('table', { sombrear: !ed.getAttributes('table').sombrear }).run(); }}>Sombrear a linha de cabeçalho</ItemMenu>
              <ItemMenu marcado={!!ed.getAttributes('table').repetirCabecalho} detalhe="No PDF, no Word e na impressão" onClick={() => { fechar(); ed.chain().focus().updateAttributes('table', { repetirCabecalho: !ed.getAttributes('table').repetirCabecalho }).run(); }}>Repetir o cabeçalho em cada página</ItemMenu>
              <SepMenu />
              <ItemMenu onClick={() => { fechar(); ed.chain().focus().toggleHeaderRow().run(); }}>Ligar ou desligar a linha de cabeçalho</ItemMenu>
            </>
          )}
        </MenuBotao>
      </Grupo>
      <Grupo>
        <MenuBotao dica={{ nome: 'Fórmula', desc: 'SOMA, MÉDIA, MÁXIMO, MÍNIMO e contas entre células, como no Word. Recalcula sozinha.' }} icone={ico(Sigma)} rotulo="Fórmula" desativado={fora}>
          {(fechar) => (
            <>
              <TituloMenu>Inserir na célula do cursor</TituloMenu>
              {[['=SOMA(ACIMA)', 'Soma das de cima'], ['=SOMA(ESQUERDA)', 'Soma das da esquerda'], ['=MÉDIA(ACIMA)', 'Média'], ['=MÁXIMO(ACIMA)', 'Maior valor'], ['=MÍNIMO(ACIMA)', 'Menor valor'], ['=CONTAR(ACIMA)', 'Quantidade']].map(([fx, nome]) => (
                <ItemMenu key={fx} detalhe={fx} onClick={() => { fechar(); ed.chain().focus().definirFormula(fx).run(); }}>{nome}</ItemMenu>
              ))}
              <SepMenu />
              <TextoMenu>Conta livre na barra de fórmula: =B2-C2, =D5*1,1. Colunas são letras e linhas são números, como no Word.</TextoMenu>
            </>
          )}
        </MenuBotao>
        <div className="cm-ef-formula" aria-label="Barra de fórmula">
          <span className="cm-ef-ref">{est.referencia ?? '–'}</span>
          <span className="cm-ef-fx">fx</span>
          <input
            value={formula}
            disabled={fora}
            placeholder={fora ? 'Clique numa célula' : 'Valor ou fórmula (=…)'}
            aria-label="Fórmula da célula"
            onChange={(e) => setFormula(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ed.chain().focus().definirFormula(formula).run(); } }}
          />
        </div>
      </Grupo>
    </div>
  );
}

/* ===================== Layout ===================== */

export function BarraLayout({ ed, ac }: { ed: Editor; ac: AcoesEditor }) {
  const d = ac.documento;
  const quebra = (t: TipoQuebra) => () => ed.chain().focus().inserirQuebra(t).run();
  return (
    <div className="cm-ef-barra" role="toolbar" aria-label="Layout">
      <Grupo>
        <MenuBotao dica={{ nome: 'Margens', desc: 'ABNT, Normal, Estreita ou medidas suas.' }} icone={ico(Scan)} rotulo="Margens" largura={300}>
          {(fechar) => (
            <>
              {MARGENS_PRONTAS.map((m) => (
                <ItemMenu key={m.chave} ativo={d.nomeMargens === m.nome} detalhe={m.resumo} onClick={() => { fechar(); ac.mudarDocumento({ margens: m.margens, nomeMargens: m.nome }); }}>{m.nome}</ItemMenu>
              ))}
              <SepMenu />
              <ItemMenu onClick={() => { fechar(); ac.abrirMargens(); }}>Margens personalizadas…</ItemMenu>
            </>
          )}
        </MenuBotao>
        <MenuBotao dica={{ nome: 'Orientação' }} icone={ico(RectangleVertical)} rotulo="Orientação">
          {(fechar) => (
            <>
              <ItemMenu ativo={d.orientacao === 'retrato'} onClick={() => { fechar(); ac.mudarDocumento({ orientacao: 'retrato' }); }}>Retrato</ItemMenu>
              <ItemMenu ativo={d.orientacao === 'paisagem'} onClick={() => { fechar(); ac.mudarDocumento({ orientacao: 'paisagem' }); }}>Paisagem</ItemMenu>
            </>
          )}
        </MenuBotao>
        <MenuBotao dica={{ nome: 'Tamanho do papel' }} icone={ico(File)} rotulo="Tamanho">
          {(fechar) => (Object.keys(PAPEIS) as Papel[]).map((p) => (
            <ItemMenu key={p} ativo={d.papel === p} detalhe={`${fmtNum(PAPEIS[p].largura)} × ${fmtNum(PAPEIS[p].altura)} cm`} onClick={() => { fechar(); ac.mudarDocumento({ papel: p }); }}>{PAPEIS[p].nome}</ItemMenu>
          ))}
        </MenuBotao>
        {ac.paginaPadrao && (
          <MenuBotao dica={{ nome: 'Página padrão', desc: 'O papel, a orientação e as margens com que as próximas peças abrem.' }} icone={ico(FilePlus2)} rotulo="Padrão" largura={320}>
            {(fechar) => {
              const pp = ac.paginaPadrao!;
              return (
                <>
                  <TextoMenu>{pp.ehEsta
                    ? 'A página desta peça já é a das próximas peças.'
                    : `As próximas peças abrem com ${pp.guardada ? 'a página padrão guardada' : 'A4, retrato, margens ABNT'}.`}</TextoMenu>
                  <ItemMenu ativo={pp.ehEsta} detalhe={`${PAPEIS[d.papel].nome} ${d.orientacao} · margens ${d.nomeMargens}`} onClick={() => { fechar(); if (!pp.ehEsta) pp.usar(); }}>
                    Usar esta página nas próximas peças
                  </ItemMenu>
                  {pp.guardada && <ItemMenu detalhe="A4, retrato, margens ABNT" onClick={() => { fechar(); pp.voltar(); }}>Voltar à página do app</ItemMenu>}
                </>
              );
            }}
          </MenuBotao>
        )}
      </Grupo>
      <Grupo>
        <MenuBotao dica={{ nome: 'Colunas', desc: 'Divide o texto em colunas.' }} icone={ico(Columns2)} rotulo="Colunas">
          {(fechar) => (
            <>
              {[1, 2, 3].map((n) => <ItemMenu key={n} ativo={d.colunas === n} onClick={() => { fechar(); ac.mudarDocumento({ colunas: n }); }}>{n === 1 ? 'Uma' : n === 2 ? 'Duas' : 'Três'}</ItemMenu>)}
              <SepMenu />
              <ItemMenu marcado={d.linhaEntreColunas} onClick={() => ac.mudarDocumento({ linhaEntreColunas: !d.linhaEntreColunas })}>Linha entre as colunas</ItemMenu>
              <TextoMenu>Quebra de coluna (Ctrl+Shift+Enter) leva o texto para a coluna seguinte.</TextoMenu>
            </>
          )}
        </MenuBotao>
        <MenuBotao dica={{ nome: 'Quebras', desc: 'Página, coluna e seção.' }} icone={ico(SeparatorHorizontal)} rotulo="Quebras">
          {(fechar) => (
            <>
              <ItemMenu atalho="Ctrl+Enter" onClick={() => { fechar(); quebra('pagina')(); }}>Quebra de página</ItemMenu>
              <ItemMenu atalho="Ctrl+Shift+Enter" onClick={() => { fechar(); quebra('coluna')(); }}>Quebra de coluna</ItemMenu>
              <ItemMenu detalhe="próxima página" onClick={() => { fechar(); quebra('secao')(); }}>Quebra de seção</ItemMenu>
              <ItemMenu detalhe="contínua" onClick={() => { fechar(); quebra('secaoContinua')(); }}>Quebra de seção</ItemMenu>
            </>
          )}
        </MenuBotao>
      </Grupo>
      <Grupo>
        <Botao dica={{ nome: 'Papel timbrado', desc: 'O timbrado do escritório no cabeçalho e no rodapé. Cadastre uma vez; escolha em cada peça.' }} icone={ico(Stamp)} rotulo="Papel timbrado" variante="forte" onClick={ac.abrirTimbrado} />
      </Grupo>
    </div>
  );
}

/* ===================== Revisar ===================== */

export function BarraRevisar({ est, ac }: { est: EstadoEditor; ac: AcoesEditor }) {
  return (
    <div className="cm-ef-barra" role="toolbar" aria-label="Revisar">
      <Grupo>
        <MenuBotao dica={{ nome: 'Ortografia e gramática', atalho: 'F7', desc: 'Corretor do navegador, mais o dicionário pt-BR embutido com termos jurídicos e o seu dicionário.' }} icone={ico(SpellCheck)} rotulo="Ortografia">
          {(fechar) => (
            <>
              <ItemMenu atalho="F7" onClick={() => { fechar(); ac.verificarOrtografia(); }}>Verificar agora</ItemMenu>
              <SepMenu />
              <ItemMenu marcado={est.corretorNativo} onClick={() => ac.definirCorretorNativo(!est.corretorNativo)}>Corretor do navegador</ItemMenu>
              {ac.temOrtografiaEmbutida && <ItemMenu marcado={est.ortografiaAtiva} onClick={() => ac.definirOrtografia(!est.ortografiaAtiva)}>Dicionário pt-BR embutido</ItemMenu>}
              <SepMenu />
              <TituloMenu>Meu dicionário ({ac.dicionarioPessoal.length})</TituloMenu>
              {ac.dicionarioPessoal.slice(-6).map((p) => <span key={p} className="cm-ef-op cm-ef-op-txt is-estatico">{p}</span>)}
              <ArquivoMenu rotulo="Importar dicionário… (.dic, .txt)" aceitar=".dic,.txt,.csv" aoEscolher={ac.importarDicionario} />
            </>
          )}
        </MenuBotao>
        {ac.temIA && (
          <Botao
            dica={{ nome: 'Revisar com IA', desc: `Gramática, concordância e estilo forense, marcados no texto; cada sugestão você aceita ou recusa.${ac.creditosRevisaoIA ? ` ${dicaCreditosDaRevisao(ac.creditosRevisaoIA())}` : ''}` }}
            icone={ac.revisandoIA ? <Spinner size={16} className="text-inherit" /> : ico(Sparkles)}
            rotulo={ac.revisandoIA ? 'Revisando…' : 'Revisar com IA'} variante="primario" desativado={ac.revisandoIA} onClick={ac.revisarComIA}
          />
        )}
      </Grupo>
      <Grupo>
        <span className="cm-ef-contagem">{est.palavras.toLocaleString('pt-BR')} {est.palavras === 1 ? 'palavra' : 'palavras'}</span>
      </Grupo>
    </div>
  );
}
